import { useMemo } from "react";

import { endOfDay, isAfter, isBefore, startOfDay } from "date-fns";
import { utcDate } from "src/utility/utilityFunctions";

import { useAuthUser } from "@features/auth/AuthUserContext";
import { useOptionalDistributorOrderingCtx } from "@features/orderingCtx/DistributorOrderingContext";
import { useApiResource } from "@services/api";

import { getOrderableEndDateField } from "./helpers";
import { OcmDateField } from "./types";

const today = new Date();

export type OCMFieldRange = {
  startDate?: OcmDateField;
  endDate?: OcmDateField;
};

export const useLoadActiveOrderWindows = (dateRange?: OCMFieldRange) => {
  const { role } = useAuthUser();
  const distCtx = useOptionalDistributorOrderingCtx();

  const shouldFetch = role !== "supplier";

  const {
    data = [],
    error,
    isFetching,
  } = useApiResource("order-calendar-months", {
    params: {
      filter: {
        hasFutureInMarketStartDate: true,
        hasProgramsForMarketId: distCtx ? distCtx.orderingCtx.marketId : null,
        hasProgramsWithAccessType: distCtx ? "distributor" : "internal",
      },
      sort: "order-window-open-date",
      page: { size: 100 },
    },
    enabled: shouldFetch,
    staleTime: 60 * 60 * 1000,
  });

  const startDateField: OcmDateField =
    dateRange?.startDate ?? "orderWindowOpenDate";

  const endDateField: OcmDateField =
    dateRange?.endDate ?? getOrderableEndDateField(role);

  const activeOrderWindows = useMemo(
    () =>
      data.filter(
        (ocm) =>
          isBefore(startOfDay(utcDate(ocm[startDateField])), today) &&
          isAfter(endOfDay(utcDate(ocm[endDateField])), today)
      ),
    [startDateField, endDateField, data]
  );

  return { data: activeOrderWindows, error, isFetching };
};

export default function useActiveOrderWindows(dateRange?: OCMFieldRange) {
  const { data } = useLoadActiveOrderWindows(dateRange);

  return data;
}
